import { BdConfig, BdDocType, BdGap, BdMissing, BdPick, BdQuestion, BdResults, BdShareKind, BdUnclear } from "../../types/bd/Bd";

/**
 * The one decision per finding on the analysis page: Ask them, We fix it or
 * Skip. A finding without a pick shows the AI's pick (mirror of crx-be
 * services/bd/BdPicks.ts, which Finish uses to make the tasks).
 */

export type FindingKind = "gap" | "question" | "unclear" | "missing";
export type Finding = BdGap | BdQuestion | BdUnclear | BdMissing;
/** A finding with where it sits in the results, and its stable key (asks and share points use it). */
export interface FindingRef { kind: FindingKind; index: number; item: Finding; key: string; }

export const PICK_LABEL: Record<BdPick, string> = { ask: "Ask them", fix: "We fix it", skip: "Skip" };
export const KIND_LABEL: Record<FindingKind, string> = { gap: "Gap", question: "Question", unclear: "Unclear", missing: "Missing document" };

const PREFIX: Record<FindingKind, string> = { gap: "gap", question: "q", unclear: "u", missing: "m" };
const isExternal = (docType: BdDocType) => docType === "partner" || docType === "contract";

/** Stable key of a finding: its id when it has one, else its position (gap:g1, q:3, m:0). */
export const findingKey = (kind: FindingKind, item: object, index: number): string => `${PREFIX[kind]}:${(item as { id?: string }).id ?? index}`;

/**
 * The AI's pick: a gap is ours to fix, unless it carries wording for a partner
 * or contract document (that goes to them, as before); a question is asked
 * when it is in the email; an unclear point is asked; a missing document is
 * ours to get when it has a person, else we ask for it.
 */
export function aiPick(kind: FindingKind, item: Finding, docType: BdDocType): BdPick {
    if (kind === "gap") {
        const g = item as BdGap;
        return g.fix || g.ext || (isExternal(docType) && g.q) ? "ask" : "fix";
    }
    if (kind === "question") return (item as BdQuestion).inc !== false ? "ask" : "skip";
    if (kind === "unclear") return "ask";
    return (item as BdMissing).ownerId ? "fix" : "ask";
}

/** The person's pick, or the AI's while nobody changed it. */
export const pickOf = (kind: FindingKind, item: Finding, docType: BdDocType): BdPick => item.pick ?? aiPick(kind, item, docType);

/** How an "Ask them" finding goes to them: partner and contract wording stays a suggested change or a comment. */
export function shareKindOf(kind: FindingKind, item: Finding, docType: BdDocType): BdShareKind {
    if (kind !== "gap") return "Question";
    const g = item as BdGap;
    if (g.fix) return "Correction";
    return g.ext || (isExternal(docType) && g.q) ? "Comment" : "Question";
}

/** Every finding of a kind, with its key. */
export const findingsOf = (r: BdResults, kind: FindingKind): FindingRef[] => {
    const list: Finding[] = kind === "gap" ? r.gaps : kind === "question" ? r.questions : kind === "unclear" ? r.unclear : r.missing;
    return list.map((item, index) => ({ kind, index, item, key: findingKey(kind, item, index) }));
};

/** Questions CRX wrote and settles itself (not must-ask, not yours): they fold into "Done by CRX". */
export const isCrxQuestion = (q: BdQuestion): boolean => !q.must && !q.mine;

/**
 * The findings that need a person, one row each: the open gaps, the questions
 * to settle (must ask, or your own), every unclear point, and the documents
 * not ready yet. The server's summary.toDecide (BdDocumentDTO) counts the same.
 */
export const toDecide = (r: BdResults): FindingRef[] => [
    ...findingsOf(r, "gap").filter((f) => !(f.item as BdGap).done),
    ...findingsOf(r, "question").filter((f) => !isCrxQuestion(f.item as BdQuestion)),
    ...findingsOf(r, "unclear"),
    ...findingsOf(r, "missing").filter((f) => !(f.item as BdMissing).done && (f.item as BdMissing).st !== "ready"),
];

/** Who does the fix and by when (missing documents have no date). */
export const ownerOf = (item: Finding): number | null => (item as { ownerId?: number | null }).ownerId ?? null;
export const dueOf = (kind: FindingKind, item: Finding): string | null => (kind === "missing" ? null : (item as { due?: string | null }).due ?? null);

const AS: Record<BdShareKind, string> = { Question: "a question", Correction: "a suggested change", Comment: "a comment" };

/** The row's second line: who and when for "We fix it", where it goes for "Ask them", "Left out" for Skip. */
export function pickMeta(f: FindingRef, docType: BdDocType, ctx: { from: string; people: Record<number, string>; me: number | null; date: (iso: string) => string }): string {
    const pick = pickOf(f.kind, f.item, docType);
    if (pick === "skip") return "Left out";
    if (pick === "ask") return `Goes to ${ctx.from} as ${AS[shareKindOf(f.kind, f.item, docType)]}`;
    const owner = ownerOf(f.item);
    const due = dueOf(f.kind, f.item);
    const who = owner ? ctx.people[owner] ?? (owner === ctx.me ? "You" : "Someone") : "Nobody yet";
    return `${f.kind === "missing" ? `Gets it: ${who}` : who}${due ? ` · by ${ctx.date(due)}` : ""}${(f.item as { done?: boolean }).done ? " · done" : ""}`;
}

export interface TaskDraft { key: string; t: string; ownerId: number; due: string | null; }

/**
 * What Finish turns into tasks (the same rule as the server): each open
 * finding picked "We fix it" that has a person; missing documents only when
 * BD settings say so; none when BD settings turn tasks off.
 */
export function taskDrafts(r: BdResults, docType: BdDocType, tasks?: BdConfig["tasks"]): TaskDraft[] {
    if (tasks && !tasks.create) return [];
    return [...findingsOf(r, "gap"), ...findingsOf(r, "question"), ...findingsOf(r, "unclear"), ...(tasks?.missingDocs === false ? [] : findingsOf(r, "missing"))]
        .filter((f) => pickOf(f.kind, f.item, docType) === "fix" && ownerOf(f.item) && !(f.item as { done?: boolean }).done)
        .map((f) => ({
            key: f.key,
            t: f.kind === "missing" ? `Get: ${f.item.t}` : (f.item as { act?: string }).act || f.item.t,
            ownerId: ownerOf(f.item) as number,
            due: dueOf(f.kind, f.item),
        }));
}
